import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import { hexWithOpacity, toTitleCase } from 'common/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { colors } from 'styles/colors';
import { snackbarService } from 'services/snackbarService';
import { useUpdateProcedure } from 'hooks/useProcedure';
import { useAuth } from 'context/AuthContext';
import { Box } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import useStyles from './styles';
import { EInsuranceStatus } from 'enum/EInsuranceStatus';
import Circle from 'components/common/Circle/Circle';

interface Props {
  item: any;
  refetch?: () => void;
  isEditor?: boolean;
}

export default function InsuranceStatus({ item, refetch, isEditor = false }: Props) {
  const { user } = useAuth();
  const styles = useStyles();
  const popupRef = useRef<any>(null);

  const data = useMemo(() => [
    { id: '1', title: 'Approved', color: colors.success },
    { id: '2', title: 'Denied', color: colors.error },
    { id: '3', title: 'Pending', color: colors.warning },
    { id: '4', title: 'Appeal', color: colors.appeal },
  ], [colors]);


  const mutation = useUpdateProcedure((data: any) => {
    snackbarService.showSnackbar('Insurance status has been updated successfully', 'success');
    refetch?.();
  }, (error: any) => console.error('Error:', error));


  const handleSelect = (index: number) => {
    popupRef.current.close();
    mutation.mutate({
      userId: user.id,
      procedureId: item.id,
      data: {
        insurance_status: data[index].title.toUpperCase(),
        case_procedures: item.case_procedures
      }
    });
  }

  const getStatusColor = (insuranceStatus: EInsuranceStatus) => {
    const status = insuranceStatus?.toLowerCase();
    switch (status) {
      case EInsuranceStatus.APPROVED:
      case EInsuranceStatus.APPROVED.toLowerCase():
        return colors.success;
      case EInsuranceStatus.PENDING:
      case EInsuranceStatus.PENDING.toLowerCase():
        return colors.warning;
      case EInsuranceStatus.DENIED:
      case EInsuranceStatus.DENIED.toLowerCase():
        return colors.error;
      case EInsuranceStatus.APPEAL:
      case EInsuranceStatus.APPEAL.toLowerCase():
        return colors.appeal;
      default:
        return colors.text;
    }
  }

  return (
    <PopupState variant="popover" popupId="demo-popup-popover">
      {(popupState) => {
        popupRef.current = popupState;
        return (
          <div>
            <Button
              {...(isEditor ? bindTrigger(popupState) : {})}
              className={styles.statusOuterButton}
              sx={{
                paddingLeft: 0,
                backgroundColor: hexWithOpacity(getStatusColor(item.insurance_status), 0.1),
                color: getStatusColor(item.insurance_status),
                '&&:hover': {
                  bgcolor: hexWithOpacity(getStatusColor(item.insurance_status), 0.3),
                  color: getStatusColor(item.insurance_status),
                },
              }}
            >
              <Circle
                boxSize={20}
                circleSize={6}
                color={getStatusColor(item.insurance_status)}
              />
              {toTitleCase(item?.insurance_status)}
            </Button>
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              {data.map((option, index) => (
                <Box
                  key={index}
                  className={styles.statusButton}
                  sx={{
                    backgroundColor: hexWithOpacity(option.color, 0.1),
                    '&:hover': {
                      backgroundColor: hexWithOpacity(option.color, 0.35),
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => handleSelect(index)}
                >
                  <Circle
                    boxSize={25}
                    circleSize={8}
                    color={option.color} />
                  <CustomText
                    text={option.title}
                    color={option.color}
                    fontSize={13}
                  />
                </Box>
              ))}
            </Popover>
          </div>
        )
      }}
    </PopupState>
  );
}
